import { pv } from '../motion'
import { Desk, Heart, INK, Person, Plant } from '../v3/figures'
import { IsoRect, iso } from '../v3/iso'
import {
  Chair,
  DESKS,
  Floor,
  FloorLabels,
  Lounge,
  Machine,
  PLANTS,
  Walls
} from '../v3/world'
import { SCAN, Scanner, TALENTS, scanTime } from '../v4/world'
import { atB, inB } from './beats'

/**
 * Das Büro der Version 5. Grundriss wie in Version 4 (freier Platz im
 * Vertrieb, Fläche für die Bewerbenden), Zeitpunkte aus der Kombi-Zeitleiste.
 */

const STAFF = [
  {
    id: 'vLead',
    x: 4.4,
    y: 12.6,
    body: '#4461D3',
    skin: '#C68B59',
    hair: '#2B1D14',
    happy: inB('analyse', 0.76, 0.84),
    label: 'Vertrieb'
  },
  {
    id: 'vMate',
    x: 4.4,
    y: 16.6,
    body: '#8FA4D8',
    skin: '#F1D3B3',
    hair: '#E6C27A',
    happy: inB('kreuz', 0.75, 0.85)
  },
  {
    id: 'csLead',
    x: 18.3,
    y: 11.9,
    body: 'green',
    skin: '#8D5524',
    hair: INK,
    happy: inB('analyse', 0.72, 0.8),
    sad: [...inB('ohne', 0.74, 0.8), ...inB('rewind', 0.2, 0.5)],
    label: 'CS Lead'
  },
  {
    id: 'csMate',
    x: 21.8,
    y: 11.9,
    body: 'yellow',
    skin: '#F2C9A5',
    hair: '#7A4A2A',
    happy: inB('analyse', 0.8, 0.88)
  },
  {
    id: 'csNew',
    x: 18.3,
    y: 16.4,
    body: '#546ED4',
    skin: '#E0AC69',
    hair: '#2B1D14',
    happy: inB('team', 0.62, 0.74)
  }
]

export const STAFF_BY_ID = Object.fromEntries(STAFF.map((p) => [p.id, p]))

const LABELS = [
  { text: 'Vertrieb', x: 0.9, y: 9.4, size: 30 },
  { text: 'Customer Success', x: 16.9, y: 8.0, size: 30 },
  { text: 'Bewerbende', x: 8.8, y: 20.9, size: 28 },
  { text: 'HR', x: 8.7, y: 11.2, size: 24 }
]

// Der Würfel fehlt in der Rückblende, kommt beim Zurückspulen dazu und wird
// beim Blick in Aivy kurz zum Schnittmodell
const AIVY = {
  ...pv('av', inB('rewind', 0.55, 0.85)),
  opacity: 'var(--av)'
}
const GLASS = {
  ...pv('m', inB('magic', 0.04, 0.16)),
  ...pv('n', inB('magic', 0.9, 1)),
  opacity: 'calc(1 - 0.8 * var(--m) * (1 - var(--n)))'
}
export const MACHINE_LABEL = {
  ...pv('av', inB('rewind', 0.55, 0.85)),
  ...GLASS,
  opacity: 'calc(var(--av) * (1 - 0.8 * var(--m) * (1 - var(--n))))'
}

export const SCAN_AT = inB('regeln', 0.14, 0.7)

/** Bewerbungen landen, danach wird aus jedem Brief ein Mensch. */
export const spawnAt = (i) => {
  const start = atB('bewerbungen', 0.6 + i * 0.022)
  return [start, start + 0.006]
}

// Smartphone mit dem Assessment kommt aus dem Würfel geflogen
export const phoneLanding = (i) => atB('games', 0.24 + i * 0.012)

// Stärkenprofil fliegt nach den Spielen zu jeder Person
export const docLanding = (i) => atB('talente', 0.2 + i * 0.018)

export const talentTimes = (t, i) => {
  const land = phoneLanding(i)
  const doc = docLanding(i)
  return {
    happy: inB('talente', 0.26 + i * 0.012, 0.34 + i * 0.012),
    ring: [scanTime(t.x, SCAN_AT), scanTime(t.x, SCAN_AT) + 0.003],
    phone: [[land - 0.002, land], inB('games', 0.84, 0.9)],
    shape: (k) => inB('games', 0.82 + k * 0.04, 0.9 + k * 0.04),
    doc: [doc - 0.002, doc]
  }
}

export { SCAN, TALENTS }

export function OfficeWorld5() {
  const desks = [...DESKS, { x: 2, y: 19, w: 1.3, d: 2.2 }]
  const items = [
    ...desks.map((d) => ({
      depth: d.x + (d.w || 2.4) + d.y + (d.d || 1.3),
      node: <Desk {...d} />
    })),
    ...PLANTS.filter((p) => !(p.y > 20)).map((p) => ({
      depth: p.x + p.y + 1.6,
      node: <Plant {...p} />
    })),
    ...STAFF.map((p) => {
      const s = iso(p.x, p.y)
      return {
        depth: p.x + p.y + 0.4,
        node: (
          <g transform={`translate(${s.x} ${s.y})`}>
            <Person {...p} />
          </g>
        )
      }
    }),
    {
      depth: 12,
      node: (
        <g style={AIVY}>
          <Machine style={GLASS} />
        </g>
      )
    },
    { depth: 37.6, node: <Chair x={21.4} y={15.5} /> },
    { depth: 23.4, node: <Chair x={3.4} y={19.5} /> },
    { depth: 41, node: <Lounge /> }
  ].sort((a, b) => a.depth - b.depth)

  return (
    <g>
      <Floor />
      <Walls />
      <IsoRect x={8.4} y={14.4} w={7.6} d={6.8} fill='#FFF3D6' />
      <IsoRect x={20} y={17.4} w={6} d={4.6} fill='#FDE6EF' />
      <IsoRect x={16.6} y={8.8} w={7} d={8.6} fill='#E4F8F3' />
      <IsoRect x={0.4} y={10} w={6.2} d={11.8} fill='#E6ECFF' />
      <FloorLabels labels={LABELS} />
      <Scanner at={SCAN_AT} />
      {items.map((it, i) => (
        <g key={i}>{it.node}</g>
      ))}
      {STAFF.map((p) => {
        const s = iso(p.x, p.y)
        return (
          <Heart
            key={p.id}
            x={s.x}
            y={s.y - 78}
            at={inB('finale', 0.1, 0.25)}
          />
        )
      })}
    </g>
  )
}
